import type { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { PillButton } from '@sorare/core/src/atoms/buttons/PillButton';
import { Scarcities } from '@sorare/core/src/atoms/icons/Scarcities/index.tsx';
import { iconChevronLeft } from '@sorare/core/src/atoms/icons/system/iconChevronLeft.ts';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { SecondaryPageBackground } from '@sorare/core/src/atoms/ui/SecondaryPageBackground';
import { EssenceBalance } from '@sorare/core/src/components/balance/EssenceBalance/index.tsx';
import { InGameCurrencyBalance } from '@sorare/core/src/components/balance/InGameCurrencyBalance/index.tsx';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser/index.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import {
  formatScarcity,
  playableBlockchainRarities,
} from '@sorare/core/src/lib/cards';
import { getRarityRGBColor } from '@sorare/core/src/lib/colors';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity.ts';

import { SecondaryPageHeader } from '#src/pages/PlayMissionsPageLayout/SecondaryPageHeader/index.tsx';

const Root = styled.div`
  position: relative;
  isolation: isolate;
  min-height: var(--100vh);
`;

const StyledSBHorizontal = styled(SBHorizontal)`
  padding: var(--unit) 0;
`;

const BottomBar = styled.div`
  position: fixed;
  bottom: max(
    var(--double-unit),
    env(safe-area-inset-bottom, var(--double-unit))
  );
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
`;

type Props = {
  children: ReactNode;
  rarity: Rarity;
  categoryIcon?: ReactNode;
  categoryTitle: ReactNode;
  goBack: () => void;
  onSelectRarity: (rarity: Rarity) => void;
  unclaimedTasksByRarity?: { [k in Rarity]?: number };
  overlayMidColor?: string;
};

export const ProMissionsPageLayout = ({
  children,
  rarity,
  categoryIcon,
  categoryTitle,
  goBack,
  onSelectRarity,
  unclaimedTasksByRarity,
  overlayMidColor = '#30D63E',
}: Props) => {
  const { sport } = useSportContext();
  const { lifecycle, update } = useLifecycle();
  const { currentUser } = useCurrentUserContext();
  const xpBalance = currentUser?.xpBalances?.find(
    ({ currency }) => inGameCurrencyToRarity[currency] === rarity
  );
  const shardsValue =
    currentUser?.cardShardsChests
      ?.filter(chest => chest.rarity === rarity)
      .reduce((sum, chest) => sum + chest.cardShardsCount, 0) ?? 0;

  return (
    <Root>
      <SecondaryPageBackground overlayMidColor={overlayMidColor} />
      <Container>
        <StyledSBHorizontal>
          <IconButton
            size="medium"
            color="quaternary"
            icon={iconChevronLeft}
            onClick={goBack}
          />
          <Horizontal>
            {!!xpBalance && (
              <InGameCurrencyBalance
                currency={xpBalance.currency}
                amount={
                  <FormattedNumber
                    value={xpBalance.amount}
                    notation="compact"
                  />
                }
              />
            )}
            <EssenceBalance rarity={rarity} value={shardsValue} />
          </Horizontal>
        </StyledSBHorizontal>
        <Vertical gap={5}>
          <SecondaryPageHeader
            icon={categoryIcon}
            title={categoryTitle}
            subtitle={
              <FormattedMessage defaultMessage="Get rewarded by playing daily" />
            }
          />
          {children}
        </Vertical>
      </Container>
      <BottomBar>
        <Horizontal gap={1} center>
          {playableBlockchainRarities.map(R => (
            <Badge
              key={R}
              badgeContent={unclaimedTasksByRarity?.[R]}
              size="small"
            >
              <PillButton
                selected={R === rarity}
                icon={<Scarcities rarities={[R]} />}
                label={formatScarcity(R as Rarity)}
                colorRgb={getRarityRGBColor(R as Rarity)}
                viewTransitionName={`rarity-picker-${R}`}
                onClick={() => {
                  update(LIFECYCLE.missionsRarityFilter, {
                    ...lifecycle?.missionsRarityFilter,
                    [sport]: R,
                  });
                  onSelectRarity(R as Rarity);
                }}
              />
            </Badge>
          ))}
        </Horizontal>
      </BottomBar>
    </Root>
  );
};
